'use client';

import React, { useCallback } from 'react';
import { useSelector } from '@legendapp/state/react';
import { Alert, ALERT_VARIANTS } from '@kidslabs/mario-nextjs-react-ui-core';
import { flashMessageAPI, type FlashMessage } from '@kidslabs/module-core';
import styles from './flash-message-stack.module.css';

/**
 * Map flash message type to Alert type
 */

/**
 * Individual flash message item with auto-dismiss
 */
function FlashMessageItem({ message }: { message: FlashMessage }) {
    const handleDismiss = useCallback(() => {
        flashMessageAPI.dismiss(message.id);
    }, [message.id]);

    return (
        <div className={styles.itemClassName}>
            <Alert
                type={message.type}
                title={message.title}
                message={message.message}
                onDismiss={handleDismiss}
                duration={message.duration}
                autoClose={Boolean(message.duration && message.duration > 0)}
                animationDuration={message.animationDuration}
                animation={message.animation}
                showIcon
                variant={ALERT_VARIANTS.INLINE}
            />
        </div>
    );
}

/**
 * FlashMessageStack - Renders stacked flash messages at the top of the viewport
 *
 * Subscribes reactively to the flash message store.
 * Each message auto-dismisses after its duration and can be manually closed.
 *
 * Place this component once in your root layout (e.g., LayoutPlatform).
 */
export function FlashMessageStack() {
    const messages = useSelector(() => flashMessageAPI.getMessagesObservable().get() ?? []);

    if (!messages || messages.length === 0) {
        return null;
    }

    return (
        <div className={styles.container} role="status" aria-live="polite" aria-label="Notifications">
            {messages.map((message: FlashMessage) => (
                <FlashMessageItem key={message.id} message={message} />
            ))}
        </div>
    );
}
